import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/shared/hooks/theme-context';
import { cn } from '@/shared/helpers/cn.helper';

/**
 * Bascule de thème dark/light « soleil/lune » — L'Arcanum §3.6/§3.7/§3.8.
 *
 * Bouton icon-only accessible (`aria-pressed`, `aria-label`), cible tactile de
 * 44 px (`size-11`), icônes Lucide `Sun`/`Moon` superposées (stroke 1.75) :
 * - thème clair actif → le soleil est plein centre, la lune est éclipsée
 *   (rotation + opacité 0) ;
 * - thème sombre actif → la lune est plein centre, le soleil est éclipsé.
 *
 * La transition respecte `prefers-reduced-motion` (aucun mouvement).
 */
export function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-pressed={isLight}
      aria-label={isLight ? 'Passer en thème sombre' : 'Passer en thème clair'}
      title={isLight ? 'Passer en thème sombre' : 'Passer en thème clair'}
      className="group grid size-11 shrink-0 place-items-center rounded-full border border-hairline bg-surface text-ink transition-colors hover:bg-surface-3 hover:text-ink focus-visible:outline-accent"
    >
      <span className="relative grid size-6 place-items-center" aria-hidden="true">
        {/* Soleil — visible quand le thème clair est actif */}
        <Sun
          className={cn(
            'absolute size-6 transition-all duration-300 ease-out motion-reduce:transition-none',
            isLight ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-50 opacity-0',
          )}
          strokeWidth={1.75}
        />
        {/* Lune — visible quand le thème sombre est actif */}
        <Moon
          className={cn(
            'absolute size-6 transition-all duration-300 ease-out motion-reduce:transition-none',
            isLight ? '-rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100',
          )}
          strokeWidth={1.75}
        />
      </span>
    </button>
  );
}
